import React, { useRef, useEffect, useState, useCallback } from 'react'

interface InteractionState {
  isDragging: boolean;
  isResizing: boolean;
  isRotating: boolean;
  selectedIndex: number; // 当前选中的元素索引
  draggedIndex: number; // 当前被拖动的元素索引
  dragStartPos: { x: number, y: number };
  initialSize?: number;
  initialRotation?: number;
  resizeHandle?: string;
}

interface CanvasProps {
  countrys: any[];
  mode: string;
  onUpdatePosition?: (index: number, deltaX: number, deltaY: number) => void;
  onUpdateSize?: (index: number, newSize: number) => void;
  onUpdateRotation?: (index: number, newRotation: number) => void;
  selectedIndex?: number;
  onSelectionChange?: (index: number) => void;
  style?: any;
}

const Canvas = (props: CanvasProps) => {

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [interactionState, setInteractionState] = useState<InteractionState>({
    isDragging: false,
    isResizing: false,
    isRotating: false,
    selectedIndex: -1, // -1 表示没有选中任何元素
    draggedIndex: -1, // -1 表示没有拖动任何元素
    dragStartPos: { x: 0, y: 0 }
  })

  // 获取元素的选中框边界（与绘制的选中框完全一致）
  const getElementBounds = useCallback((country: any) => {
    if (country.type === "country") {
      const elementX = country.position.x - country.size / 2;
      const elementY = country.position.y - country.size * country.img.height / country.img.width / 2;
      const elementWidth = country.size;
      const elementHeight = country.size * country.img.height / country.img.width;
      
      return { x: elementX, y: elementY, width: elementWidth, height: elementHeight };
    } else if (country.type === "chat" && country.chatText) {
      const scale = country.size || 1.0;
      const baseDialogX = country.position.x - 20 * scale;
      const baseDialogY = country.position.y - 50 * scale;
      const lines = country.chatText.split('\n');
      const baseDialogWidth = (Math.max(50, Math.max(...lines.map((line: string) => line.length * 20))) + 40) * scale;
      const baseDialogHeight = (40 * (lines.length + 1)) * scale;
      
      return { x: baseDialogX, y: baseDialogY, width: baseDialogWidth, height: baseDialogHeight };
    }
    return { x: 0, y: 0, width: 0, height: 0 };
  }, []);

  // 检查点击是否在元素的选中框范围内（使用与绘制选中框完全相同的边界）
  const isPointInElement = useCallback((x: number, y: number, country: any) => {
    const bounds = getElementBounds(country);
    return x >= bounds.x && x <= bounds.x + bounds.width && 
           y >= bounds.y && y <= bounds.y + bounds.height;
  }, [getElementBounds]);

  // 检查是否点击在缩放控制点上
  const getResizeHandle = useCallback((x: number, y: number, country: any) => {
    if (country.type !== "country" && country.type !== "chat") return null;
    
    const bounds = getElementBounds(country);
    const handleSize = window.innerWidth <= 768 ? 16 : 8; // 移动端使用更大的控制点
    const handles = [
      { name: 'nw', x: bounds.x - handleSize/2, y: bounds.y - handleSize/2 },
      { name: 'ne', x: bounds.x + bounds.width - handleSize/2, y: bounds.y - handleSize/2 },
      { name: 'sw', x: bounds.x - handleSize/2, y: bounds.y + bounds.height - handleSize/2 },
      { name: 'se', x: bounds.x + bounds.width - handleSize/2, y: bounds.y + bounds.height - handleSize/2 }
    ];
    
    for (const handle of handles) {
      if (x >= handle.x && x <= handle.x + handleSize && 
          y >= handle.y && y <= handle.y + handleSize) {
        return handle.name;
      }
    }
    return null;
  }, [getElementBounds]);

  // 检查是否点击在旋转控制点上
  const getRotateHandle = useCallback((x: number, y: number, country: any) => {
    if (country.type !== "country" && country.type !== "chat") return null;
    
    const bounds = getElementBounds(country);
    const rotateDistance = 30; // 旋转控制点距离边框的距离
    const handleRadius = window.innerWidth <= 768 ? 12 : 8; // 旋转控制点半径
    
    // 旋转控制点位于右侧中间位置
    const rotateX = bounds.x + bounds.width + rotateDistance;
    const rotateY = bounds.y + bounds.height / 2;
    
    // 检查点击是否在圆形控制点内
    const distance = Math.sqrt((x - rotateX) ** 2 + (y - rotateY) ** 2);
    return distance <= handleRadius ? 'rotate' : null;
  }, [getElementBounds]);

  const draw = useCallback((ctx: any) => {
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    const countrys = props.countrys;
    ctx.fillStyle = '#000000';
    countrys.forEach((country: any) => {
      if (country.type === "country") {
        const rotation = (country.rotation || 0) * Math.PI / 180; // 转换为弧度
        
        // 保存当前变换状态
        ctx.save();
        
        // 如果有旋转，应用变换
        if (rotation !== 0) {
          ctx.translate(country.position.x, country.position.y);
          ctx.rotate(rotation);
          ctx.translate(-country.position.x, -country.position.y);
        }

        if (country.img.complete) {
          ctx.drawImage(country.img, 0, 0, country.img.width, country.img.height,
            country.position.x - country.size / 2, country.position.y - country.size * country.img.height / country.img.width / 2,
            country.size, country.size * country.img.height / country.img.width);
        } else {
          country.img.onload = function () {
            ctx.drawImage(country.img, 0, 0, country.img.width, country.img.height,
              country.position.x - country.size / 2, country.position.y - country.size * country.img.height / country.img.width / 2,
              country.size, country.size * country.img.height / country.img.width);
          }
        }

        if (country.expressionImg.complete) {
          ctx.drawImage(country.expressionImg, 0, 0, country.expressionImg.width, country.expressionImg.height,
            country.position.x - country.size * 1.4 / 2, country.position.y - country.size * 1.4 / 2,
            country.size * 1.4, country.size * 1.4);
        } else {
          country.expressionImg.onload = function () {
            ctx.drawImage(country.expressionImg, 0, 0, country.expressionImg.width, country.expressionImg.height,
              country.position.x - country.size * 1.4 / 2, country.position.y - country.size * 1.4 / 2,
              country.size * 1.4, country.size * 1.4);
          }
        }
        
        // 恢复变换状态
        ctx.restore();
      } else if (country.type === "chat") {
        if (country.chatText) {
          const scale = country.size || 1.0;
          const rotation = (country.rotation || 0) * Math.PI / 180; // 转换为弧度
          
          ctx.font = (30 * scale) + 'px shufa'; // 字体大小根据缩放系数调整
          const text = country.chatText;

          let width = 50 * scale;

          // 新增换行符处理
          const lines: any[] = [];
          text.split('\n').forEach((paragraph: any) => {
            lines.push(paragraph);
            width = Math.max(width, ctx.measureText(paragraph).width);
          });
          width += 40 * scale;
          const dialogWidth = width;
          const dialogHeight = (40 * (lines.length + 1)) * scale;
          const dialogX = country.position.x - 20 * scale;
          const dialogY = country.position.y - 50 * scale;
          const radius = 30 * scale;
          
          ctx.save();
          
          // 如果有旋转，应用变换
          if (rotation !== 0) {
            ctx.translate(country.position.x, country.position.y);
            ctx.rotate(rotation);
            ctx.translate(-country.position.x, -country.position.y);
          }
          ctx.fillStyle = "#FFFFFF"; // 白色背景
          ctx.shadowColor = 'rgba(0, 0, 0, 0.1)'; // 阴影颜色（灰色，半透明）
          ctx.shadowBlur = 3; // 阴影模糊度
          ctx.shadowOffsetX = 0; // 阴影水平偏移
          ctx.shadowOffsetY = 1; // 阴影垂直偏移
          // 使用 arcTo 方法绘制圆角矩形
          ctx.beginPath();
          ctx.moveTo(dialogX + radius, dialogY); // 起点在左上角圆角的右上方
          ctx.arcTo(dialogX + dialogWidth, dialogY, dialogX + dialogWidth, dialogY + dialogHeight, radius);  // 上边到右边的圆角
          if (country.chatType === "left_chat") {
            ctx.arcTo(dialogX + dialogWidth, dialogY + dialogHeight, dialogX, dialogY + dialogHeight, radius); // 右边到下边的圆角
            ctx.arcTo(dialogX + 20, dialogY + dialogHeight, dialogX + 20, dialogY + dialogHeight, 3); // 下边到左边的圆角
            ctx.arcTo(dialogX, dialogY + dialogHeight + 20, dialogX, dialogY + dialogHeight + 20, 3); // 下边到左边的圆角
          } else if (country.chatType === "right_chat") {
            ctx.arcTo(dialogX + dialogWidth, dialogY + dialogHeight + 20, dialogX, dialogY + dialogHeight + 20, 3); // 右边到下边的圆角
            ctx.arcTo(dialogX + dialogWidth - 20, dialogY + dialogHeight, dialogX - 20, dialogY + dialogHeight, 3); // 右边到下边的圆角
            ctx.arcTo(dialogX, dialogY + dialogHeight, dialogX, dialogY, radius); // 下边到左边的圆角
          }
          ctx.arcTo(dialogX, dialogY, dialogX + dialogWidth, dialogY, radius); // 左边到上边的圆角
          ctx.closePath();
          ctx.fill();
          // 添加黑色边框
          ctx.strokeStyle = '#000000';
          ctx.lineWidth = 4;
          ctx.stroke();

          // 渲染文本（不要恢复变换状态，保持旋转效果）
          ctx.fillStyle = '#333'; // 字体颜色
          ctx.textAlign = 'left'; // 文本对齐方式
          // 渲染文本
          lines.forEach((lineText: any, index) => {
            ctx.fillText(lineText, country.position.x, country.position.y + index * 35 * scale);
          });
          
          ctx.restore(); // 在所有绘制完成后恢复变换状态
        }


      }
    });

    // 绘制选中元素的控制点（仅在编辑模式下显示）
    if (props.mode === 'edit' && interactionState.selectedIndex >= 0 && interactionState.selectedIndex < countrys.length) {
      const selectedCountry = countrys[interactionState.selectedIndex];
      if (selectedCountry) {
        // 使用统一的边界计算函数绘制选中框
        const bounds = getElementBounds(selectedCountry);
        
        // 绘制选中边框
        ctx.strokeStyle = '#007bff';
        ctx.lineWidth = 2;
        ctx.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);
        
        // 国家球和对话框都显示缩放控制点，且只在非拖动状态时显示
        if ((selectedCountry.type === "country" || selectedCountry.type === "chat") && !interactionState.isDragging) {
          const handleSize = window.innerWidth <= 768 ? 16 : 8;
          ctx.fillStyle = '#007bff';
          const handles = [
            { x: bounds.x - handleSize/2, y: bounds.y - handleSize/2 },
            { x: bounds.x + bounds.width - handleSize/2, y: bounds.y - handleSize/2 },
            { x: bounds.x - handleSize/2, y: bounds.y + bounds.height - handleSize/2 },
            { x: bounds.x + bounds.width - handleSize/2, y: bounds.y + bounds.height - handleSize/2 }
          ];
          
          handles.forEach(handle => {
            ctx.fillRect(handle.x, handle.y, handleSize, handleSize);
          });
          
          // 绘制旋转控制点（右侧圆形）
          const rotateDistance = 30;
          const rotateRadius = window.innerWidth <= 768 ? 12 : 8;
          const rotateX = bounds.x + bounds.width + rotateDistance;
          const rotateY = bounds.y + bounds.height / 2;
          
          ctx.fillStyle = '#28a745'; // 绿色表示旋转控制点
          ctx.beginPath();
          ctx.arc(rotateX, rotateY, rotateRadius, 0, 2 * Math.PI);
          ctx.fill();
          
          // 在旋转控制点上绘制旋转图标（简单的弧形）
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(rotateX, rotateY, rotateRadius * 0.6, -Math.PI/4, Math.PI * 3/4);
          ctx.stroke();
        }
      }
    }
  }, [props.countrys, props.mode, interactionState.selectedIndex, interactionState.isDragging, getElementBounds])

  // 获取鼠标或触摸在画布上的坐标（考虑缩放比例）
  const getEventPos = useCallback((e: MouseEvent | TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    let clientX, clientY;
    
    if ('touches' in e && e.touches.length > 0) {
      // 触摸事件
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else if ('clientX' in e) {
      // 鼠标事件
      clientX = e.clientX;
      clientY = e.clientY;
    } else {
      return { x: 0, y: 0 };
    }
    
    // 计算缩放比例：Canvas内部坐标 / 实际显示尺寸
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  }, []);

  // 通用的开始事件处理（鼠标按下或触摸开始）
  const handleStart = useCallback((e: MouseEvent | TouchEvent) => {
    if (props.mode !== 'edit') return;
    
    e.preventDefault(); // 防止触摸时的默认行为
    const eventPos = getEventPos(e);
    const countrys = props.countrys;
    
    // 首先检查是否点击在已选中元素的缩放控制点上
    if (interactionState.selectedIndex >= 0 && interactionState.selectedIndex < countrys.length) {
      const selectedCountry = countrys[interactionState.selectedIndex];
      if (selectedCountry && (selectedCountry.type === "country" || selectedCountry.type === "chat")) {
        const resizeHandle = getResizeHandle(eventPos.x, eventPos.y, selectedCountry);
        if (resizeHandle) {
          // 开始缩放操作
          setInteractionState({
            isDragging: false,
            isResizing: true,
            isRotating: false,
            selectedIndex: interactionState.selectedIndex,
            draggedIndex: -1,
            dragStartPos: eventPos,
            initialSize: selectedCountry.size || 1.0,
            resizeHandle
          });
          return;
        }
        
        // 检查是否点击在旋转控制点上
        const rotateHandle = getRotateHandle(eventPos.x, eventPos.y, selectedCountry);
        if (rotateHandle) {
          // 开始旋转操作
          setInteractionState({
            isDragging: false,
            isResizing: false,
            isRotating: true,
            selectedIndex: interactionState.selectedIndex,
            draggedIndex: -1,
            dragStartPos: eventPos,
            initialRotation: selectedCountry.rotation || 0
          });
          return;
        }
      }
    }
    
    // 只有选中的元素可以被拖动
    if (interactionState.selectedIndex >= 0 && interactionState.selectedIndex < countrys.length) {
      const selectedCountry = countrys[interactionState.selectedIndex];
      
      if (selectedCountry && isPointInElement(eventPos.x, eventPos.y, selectedCountry)) {
        // 只有点击选中的元素才能开始拖动
        setInteractionState(prev => ({
          ...prev,
          isDragging: true,
          isResizing: false,
          isRotating: false,
          draggedIndex: interactionState.selectedIndex, // 拖动的就是选中的元素
          dragStartPos: eventPos
        }));
        
        return;
      }
    }
    
    // 点击空白区域也不改变选中状态，选中操作只在图层列表中进行
  }, [props.mode, props.countrys, props.onSelectionChange, interactionState.selectedIndex, getEventPos, isPointInElement, getResizeHandle, getRotateHandle]);

  // 通用的移动事件处理（鼠标移动或触摸移动）
  const handleMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (props.mode !== 'edit') return;
    
    e.preventDefault(); // 防止触摸时的默认行为
    const eventPos = getEventPos(e);
    
    if (interactionState.isDragging && interactionState.draggedIndex >= 0) {
      // 拖动位置
      const deltaX = eventPos.x - interactionState.dragStartPos.x;
      const deltaY = eventPos.y - interactionState.dragStartPos.y;
      
      if (props.onUpdatePosition) {
        props.onUpdatePosition(interactionState.draggedIndex, deltaX, deltaY);
      }
      
      setInteractionState(prev => ({
        ...prev,
        dragStartPos: eventPos
      }));
    } else if (interactionState.isResizing && interactionState.selectedIndex >= 0) {
      // 缩放大小
      const country = props.countrys[interactionState.selectedIndex];
      if (country && interactionState.initialSize) {
        const deltaX = eventPos.x - interactionState.dragStartPos.x;
        const deltaY = eventPos.y - interactionState.dragStartPos.y;
        
        // 根据拖动方向计算缩放比例
        let scaleFactor = 1;
        if (interactionState.resizeHandle === 'se' || interactionState.resizeHandle === 'nw') {
          scaleFactor = 1 + (deltaX + deltaY) / 200;
        } else {
          scaleFactor = 1 + (deltaX - deltaY) / 200;
        }
        
        let newSize;
        if (country.type === "country") {
          // 国家球使用绝对尺寸
          newSize = Math.max(50, Math.min(500, interactionState.initialSize * scaleFactor));
        } else if (country.type === "chat") {
          // 对话框使用缩放系数，限制在合理范围内
          newSize = Math.max(0.3, Math.min(3.0, interactionState.initialSize * scaleFactor));
        }
        
        if (props.onUpdateSize && newSize !== undefined) {
          props.onUpdateSize(interactionState.selectedIndex, newSize);
        }
      }
    } else if (interactionState.isRotating && interactionState.selectedIndex >= 0) {
      // 旋转操作
      const country = props.countrys[interactionState.selectedIndex];
      if (country && interactionState.initialRotation !== undefined) {
        const bounds = getElementBounds(country);
        const centerX = bounds.x + bounds.width / 2;
        const centerY = bounds.y + bounds.height / 2;
        
        // 计算当前鼠标位置相对于元素中心的角度
        const currentAngle = Math.atan2(eventPos.y - centerY, eventPos.x - centerX) * 180 / Math.PI;
        const startAngle = Math.atan2(interactionState.dragStartPos.y - centerY, interactionState.dragStartPos.x - centerX) * 180 / Math.PI;
        
        // 计算旋转差值
        let deltaAngle = currentAngle - startAngle;
        
        // 处理角度跨越边界的情况
        if (deltaAngle > 180) deltaAngle -= 360;
        if (deltaAngle < -180) deltaAngle += 360;
        
        const newRotation = (interactionState.initialRotation + deltaAngle) % 360;
        
        if (props.onUpdateRotation) {
          props.onUpdateRotation(interactionState.selectedIndex, newRotation);
        }
      }
    }
  }, [props.mode, props.countrys, props.onUpdatePosition, props.onUpdateSize, props.onUpdateRotation, interactionState, getEventPos, getElementBounds]);

  // 通用的结束事件处理（鼠标松开或触摸结束）
  const handleEnd = useCallback((e: MouseEvent | TouchEvent) => {
    if (interactionState.isDragging || interactionState.isResizing || interactionState.isRotating) {
      e.preventDefault(); // 防止触摸时的默认行为
      // 结束拖动、缩放或旋转，但保持选中状态
      setInteractionState(prev => ({
        ...prev,
        isDragging: false,
        isResizing: false,
        isRotating: false,
        draggedIndex: -1 // 清除拖动索引
      }));
    }
  }, [interactionState.isDragging, interactionState.isResizing, interactionState.isRotating]);

  // 同步外部选中状态（选中状态完全由外部控制）
  useEffect(() => {
    const externalIndex = props.selectedIndex ?? -1;
    if (externalIndex !== interactionState.selectedIndex) {
      setInteractionState(prev => ({
        ...prev,
        selectedIndex: externalIndex
      }));
    }
  }, [props.selectedIndex, interactionState.selectedIndex]);

  // 当元素数组变化时，检查选中索引是否还有效
  useEffect(() => {
    if (interactionState.selectedIndex >= 0 && interactionState.selectedIndex >= props.countrys.length) {
      setInteractionState(prev => ({
        ...prev,
        selectedIndex: -1,
        isDragging: false,
        isResizing: false,
        isRotating: false,
        draggedIndex: -1
      }));
    }
  }, [props.countrys.length, interactionState.selectedIndex]);

  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d')
      const render = () => {
        draw(context)
      }
      render()
    }
  }, [draw])

  useEffect(() => {
    if (props.mode === 'edit' && canvasRef.current) {
      const canvas = canvasRef.current;
      
      // 鼠标事件
      canvas.addEventListener('mousedown', handleStart);
      canvas.addEventListener('mousemove', handleMove);
      canvas.addEventListener('mouseup', handleEnd);
      
      // 触摸事件
      canvas.addEventListener('touchstart', handleStart, { passive: false });
      canvas.addEventListener('touchmove', handleMove, { passive: false });
      canvas.addEventListener('touchend', handleEnd, { passive: false });
      
      return () => {
        canvas.removeEventListener('mousedown', handleStart);
        canvas.removeEventListener('mousemove', handleMove);
        canvas.removeEventListener('mouseup', handleEnd);
        canvas.removeEventListener('touchstart', handleStart);
        canvas.removeEventListener('touchmove', handleMove);
        canvas.removeEventListener('touchend', handleEnd);
      };
    }
  }, [props.mode, handleStart, handleMove, handleEnd]);

  return <canvas 
    ref={canvasRef} 
    {...props} 
    width={640} 
    height={640} 
    style={{ cursor: props.mode === 'edit' ? 'pointer' : 'default', ...props.style }}
  />
}

export default Canvas